import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Building,
  Layers,
  LineChart,
  MapPin,
  PieChart,
  TrendingUp,
  Users,
  Lock,
} from "lucide-react";
import {
  objectsApi,
  organizationsApi,
  objectTypesApi,
  usersApi,
  statsApi,
  rolesApi,
} from "@/services/api-v2.service";
import type { GeoObject, Organization, ObjectType, User, SystemStats, Role } from "@/types/api-v2";
import { User as UserIcon, Mail, Calendar, Edit } from "lucide-react";
import { useAuth } from "@/hooks/useAuthV2";

interface AnalyticsData {
  objectTypes: Array<{ name: string; count: number; percentage: number }>;
  activityTrend: Array<{ date: string; objects: number; geometries: number }>;
  userActivity: Array<{
    userId: string;
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    lastActive?: string;
    lastLogin?: string;
    registeredDate: string;
    objectsCreated: number;
    geometriesCreated: number;
    totalContributions: number;
    activityStatus: string;
    isActive: boolean;
    roleId?: number;
    isActiveThisWeek: boolean;
  }>;
  organizationStats: Array<{
    id: number;
    name: string;
    description?: string;
    typeId?: number;
    parentId?: number;
    objects: number;
    geometries: number;
    users: number;
    activeUsers: number;
    totalContributions: number;
    userActivityRate: number;
    activityLevel: string;
  }>;
  systemActivity: {
    actionsToday: number;
    actionsThisWeek: number;
    activeTimePercentage: number;
  };
}

interface IncidentsAnalytics {
  typeDistribution: Array<{ name: string; count: number; percentage: number; emoji: string }>;
  statusDistribution: Array<{ status: string; count: number; percentage: number; label: string }>;
  dailyActivity: Array<{ date: string; count: number }>;
  activeUsers: Array<{ userId: string; userName: string; count: number }>;
  responseTime: {
    average: number;
    fastest: number;
    slowest: number;
  };
  totalIncidents: number;
}

function aggregateObjectTypes(objects: GeoObject[], objectTypes: ObjectType[]): AnalyticsData["objectTypes"] {
  const counts: Record<number, number> = {};
  objects.forEach((obj) => {
    if (obj.kls_id) {
      counts[obj.kls_id] = (counts[obj.kls_id] || 0) + 1;
    }
  });
  const total = Math.max(objects.length, 1);
  return Object.entries(counts)
    .map(([typeId, count]) => {
      const typeName = objectTypes.find((t) => t.id === Number(typeId))?.name || `Тип #${typeId}`;
      return {
        name: typeName,
        count,
        percentage: (count / total) * 100,
      };
    })
    .sort((a, b) => b.count - a.count);
}

function aggregateActivityTrend(objects: GeoObject[]): AnalyticsData["activityTrend"] {
  const buckets: Record<string, { objects: number; geometries: number }> = {};
  objects.forEach((obj) => {
    const date = obj.created_at ? obj.created_at.split("T")[0] : "unknown";
    if (!buckets[date]) {
      buckets[date] = { objects: 0, geometries: 0 };
    }
    buckets[date].objects += 1;
  });
  return Object.entries(buckets)
    .map(([date, data]) => ({
      date,
      objects: data.objects,
      geometries: data.geometries,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function aggregateOrganizationStats(
  organizations: Organization[],
  objects: GeoObject[],
  users: User[],
): AnalyticsData["organizationStats"] {
  const objectCounts: Record<number, number> = {};
  objects.forEach((obj) => {
    if (obj.organization_id) {
      objectCounts[obj.organization_id] = (objectCounts[obj.organization_id] || 0) + 1;
    }
  });

  const userCounts: Record<number, number> = {};
  const activeUserCounts: Record<number, number> = {};
  users.forEach((user) => {
    if (user.organization_id) {
      userCounts[user.organization_id] = (userCounts[user.organization_id] || 0) + 1;
      if (user.active) {
        activeUserCounts[user.organization_id] = (activeUserCounts[user.organization_id] || 0) + 1;
      }
    }
  });

  // Подсчет геометрий (нужно будет получать из API)
  const geometriesCounts: Record<number, number> = {};

  return organizations.map((org) => {
    const objectsAmount = objectCounts[org.id] || 0;
    const geometriesAmount = geometriesCounts[org.id] || 0;
    const usersAmount = userCounts[org.id] || 0;
    const activeUsersAmount = activeUserCounts[org.id] || 0;
    const totalContributions = objectsAmount + geometriesAmount;
    const userActivityRate = usersAmount > 0 ? (activeUsersAmount / usersAmount) * 100 : 0;
    
    let activityLevel = "Низкая";
    if (objectsAmount > 10 || geometriesAmount > 20) {
      activityLevel = "Высокая";
    } else if (objectsAmount > 0 || geometriesAmount > 0) {
      activityLevel = "Средняя";
    }
    
    return {
      id: org.id,
      name: org.name,
      description: org.description || undefined,
      typeId: undefined,
      parentId: org.parent_id,
      objects: objectsAmount,
      geometries: geometriesAmount,
      users: usersAmount,
      activeUsers: activeUsersAmount,
      totalContributions,
      userActivityRate: Math.round(userActivityRate * 10) / 10,
      activityLevel,
    };
  });
}

function aggregateUserActivity(users: User[], objects: GeoObject[]): AnalyticsData["userActivity"] {
  const contributed: Record<string, number> = {};
  objects.forEach((obj) => {
    if (obj.created_by) {
      contributed[obj.created_by] = (contributed[obj.created_by] || 0) + 1;
    }
  });
  
  // Получаем статистику геометрий (нужно будет получать из API)
  const geometriesCount: Record<string, number> = {};
  
  return users.map((user) => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ") || undefined;
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    const lastActiveDate = user.last_login ? new Date(user.last_login) : (user.updated_at ? new Date(user.updated_at) : new Date(user.created_at));
    const isActiveThisWeek = lastActiveDate >= weekAgo;
    
    let activityStatus = "Нет действий";
    if (!user.active) {
      activityStatus = "Неактивен";
    } else if (contributed[user.id] || geometriesCount[user.id]) {
      if (isActiveThisWeek) {
        activityStatus = "Активен на этой неделе";
      } else {
        const daysAgo = Math.floor((Date.now() - lastActiveDate.getTime()) / (1000 * 60 * 60 * 24));
        if (daysAgo <= 30) {
          activityStatus = "Активен в этом месяце";
        } else {
          activityStatus = "Неактивен давно";
        }
      }
    }
    
    return {
      userId: user.id,
      username: user.username,
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      fullName,
      lastActive: user.last_login || user.updated_at || user.created_at,
      lastLogin: user.last_login,
      registeredDate: user.created_at,
      objectsCreated: contributed[user.id] || 0,
      geometriesCreated: geometriesCount[user.id] || 0,
      totalContributions: (contributed[user.id] || 0) + (geometriesCount[user.id] || 0),
      activityStatus,
      isActive: user.active,
      roleId: user.role_id,
      isActiveThisWeek,
    };
  });
}

function buildIncidentsStub(objects: GeoObject[]): IncidentsAnalytics {
  const total = objects.length;
  return {
    typeDistribution: [
      {
        name: "Заявки из Telegram",
        count: total,
        percentage: 100,
        emoji: "📨",
      },
    ],
    statusDistribution: [
      { status: "pending", count: total, percentage: 100, label: "На рассмотрении" },
    ],
    dailyActivity: objects.slice(0, 5).map((obj) => ({
      date: obj.created_at || "2025-11-26",
      count: 1,
    })),
    activeUsers: [
      {
        userId: "local",
        userName: "Офлайн",
        count: total,
      },
    ],
    responseTime: {
      average: 0,
      fastest: 0,
      slowest: 0,
    },
    totalIncidents: total,
  };
}

export default function Analytics() {
  const [selectedPeriod, setSelectedPeriod] = useState("30");
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, hasPermission } = useAuth();

  // Проверка прав доступа к аналитике
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // Проверяем наличие разрешения analytics.view или admin.access
      const hasAccess = hasPermission("analytics.view") || hasPermission("admin.access");
      if (!hasAccess) {
        // Перенаправляем на главную страницу, если нет доступа
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, hasPermission, navigate]);

  // Показываем загрузку пока проверяем аутентификацию
  if (authLoading) {
    return (
      <div className="min-h-screen w-full bg-background text-foreground">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <div className="text-center">
            <Activity className="h-10 w-10 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Проверка доступа...</p>
          </div>
        </div>
      </div>
    );
  }

  // Если не аутентифицирован или нет доступа, показываем сообщение
  if (!isAuthenticated || (!hasPermission("analytics.view") && !hasPermission("admin.access"))) {
    return (
      <div className="min-h-screen w-full bg-background text-foreground">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Card className="max-w-md w-full border-border">
            <CardHeader className="text-center">
              <Lock className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <CardTitle>Доступ запрещен</CardTitle>
              <CardDescription>
                У вас нет прав для просмотра аналитики. Обратитесь к администратору для получения доступа.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate("/")}>Вернуться на главную</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { data: stats, isLoading: statsLoading } = useQuery<SystemStats>({
    queryKey: ["stats"],
    queryFn: () => statsApi.getSystemStats(),
    refetchInterval: 60000,
  });

  const { data: objects = [], isLoading: objectsLoading } = useQuery<GeoObject[]>({
    queryKey: ["analytics.objects"],
    queryFn: () => objectsApi.list({ limit: 200 }),
  });

  const { data: objectTypes = [] } = useQuery<ObjectType[]>({
    queryKey: ["analytics.object-types"],
    queryFn: () => objectTypesApi.list(),
  });

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ["analytics.roles"],
    queryFn: () => rolesApi.list(),
  });

  const { data: organizations = [], isLoading: orgsLoading } = useQuery<Organization[]>({
    queryKey: ["analytics.organizations"],
    queryFn: () => organizationsApi.list({ limit: 200 }),
  });

  const { data: users = [], isLoading: usersLoading } = useQuery<User[]>({
    queryKey: ["analytics.users"],
    queryFn: () => usersApi.list({ limit: 200 }),
  });

  // Получаем детальную статистику пользователей
  const { data: userActivityStats, isLoading: _userActivityLoading } = useQuery<{
    users: AnalyticsData["userActivity"];
  }>({
    queryKey: ["analytics.user-activity"],
    queryFn: async () => {
      try {
        return await statsApi.getUserActivityStats();
      } catch (error) {
        // Если эндпоинт не доступен, используем локальную агрегацию
        return { users: aggregateUserActivity(users, objects) };
      }
    },
    enabled: !usersLoading && !objectsLoading,
  });

  // Получаем детальную статистику организаций
  const { data: organizationActivityStats, isLoading: _organizationActivityLoading } = useQuery<{
    organizations: AnalyticsData["organizationStats"];
  }>({
    queryKey: ["analytics.organization-activity"],
    queryFn: async () => {
      try {
        return await statsApi.getOrganizationActivityStats();
      } catch (error) {
        // Если эндпоинт не доступен, используем локальную агрегацию
        return { organizations: aggregateOrganizationStats(organizations, objects, users) };
      }
    },
    enabled: !orgsLoading && !objectsLoading && !usersLoading,
  });

  const isLoadingState = statsLoading || objectsLoading || orgsLoading || usersLoading;

  const analyticsData = useMemo<AnalyticsData>(() => {
    return {
      objectTypes: aggregateObjectTypes(objects, objectTypes),
      activityTrend: aggregateActivityTrend(objects),
      userActivity: userActivityStats?.users || aggregateUserActivity(users, objects),
      organizationStats: organizationActivityStats?.organizations || aggregateOrganizationStats(organizations, objects, users),
      systemActivity: {
        actionsToday: objects.length,
        actionsThisWeek: objects.length,
        activeTimePercentage: Math.min(objects.length * 10, 100),
      },
    };
  }, [objects, objectTypes, organizations, users, userActivityStats, organizationActivityStats]);

  const incidentsData = useMemo(() => buildIncidentsStub(objects), [objects]);

  if (isLoadingState) {
    return (
      <div className="min-h-screen w-full bg-background text-foreground">
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <div className="text-center">
            <Activity className="h-10 w-10 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Загрузка аналитики...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen h-full w-full bg-background text-foreground flex flex-col overflow-hidden">
      <div className="container mx-auto px-4 py-6 flex-1 min-h-0 overflow-auto space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1">
              <CardTitle className="text-sm font-medium">Всего объектов</CardTitle>
              <MapPin className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </CardHeader>
            <CardContent className="pt-2">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats?.totalObjects || 0}</div>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3" />
                +12% за месяц
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Геометрий</CardTitle>
              <Layers className="h-4 w-4 text-green-600 dark:text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats?.totalGeometries || 0}</div>
              <p className="text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3 inline mr-1" />
                +8% за месяц
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Пользователей</CardTitle>
              <Users className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats?.totalUsers || 0}</div>
              <p className="text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3 inline mr-1" />
                +2 за неделю
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Органов управления</CardTitle>
              <Building className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.totalOrganizations || 0}</div>
              <p className="text-xs text-muted-foreground">иерархия синхронизирована</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Происшествий</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{incidentsData.totalIncidents}</div>
              <p className="text-xs text-muted-foreground">
                <TrendingUp className="h-3 w-3 inline mr-1" />
                Среднее время: {incidentsData.responseTime.average}ч
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="objects" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="objects">Объекты</TabsTrigger>
            <TabsTrigger value="incidents">Происшествия</TabsTrigger>
            <TabsTrigger value="activity">Активность</TabsTrigger>
            <TabsTrigger value="users">Пользователи</TabsTrigger>
            <TabsTrigger value="organizations">Органы управления</TabsTrigger>
          </TabsList>

          <TabsContent value="objects" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Распределение по типам
                  </CardTitle>
                  <CardDescription>Анализ типов объектов в системе</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {analyticsData.objectTypes.length ? (
                    analyticsData.objectTypes.map((type) => (
                      <div key={type.name} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">{type.name}</span>
                          <Badge variant="secondary">{type.count}</Badge>
                        </div>
                        <Progress value={type.percentage} className="h-2" />
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">Нет данных о типах объектов</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LineChart className="h-5 w-5" />
                    Тренд создания
                  </CardTitle>
                  <CardDescription>Динамика создания объектов</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-sm text-muted-foreground">Период:</span>
                    <Tabs value={selectedPeriod} onValueChange={setSelectedPeriod} className="w-48">
                      <TabsList className="grid grid-cols-3">
                        <TabsTrigger value="7">7 дней</TabsTrigger>
                        <TabsTrigger value="30">30 дней</TabsTrigger>
                        <TabsTrigger value="90">90 дней</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                  <div className="space-y-3">
                    {analyticsData.activityTrend.length ? (
                      analyticsData.activityTrend.map((day) => (
                        <div key={day.date} className="flex justify-between items-center border border-border rounded-lg p-3 bg-muted/20">
                          <span className="text-sm">{new Date(day.date).toLocaleDateString("ru-RU")}</span>
                          <div className="flex gap-3">
                            <Badge variant="outline">
                              <MapPin className="h-3 w-3 mr-1" />
                              {day.objects}
                            </Badge>
                            <Badge variant="outline">
                              <Layers className="h-3 w-3 mr-1" />
                              {day.geometries}
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-sm">Нет данных об активности</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="incidents" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Распределение по типам
                  </CardTitle>
                  <CardDescription>Импортируемые инциденты</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {incidentsData.typeDistribution.map((type) => (
                    <div key={type.name} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {type.emoji} {type.name}
                        </span>
                        <Badge variant="secondary">{type.count}</Badge>
                      </div>
                      <Progress value={type.percentage} className="w-full" />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Статусы обработки
                  </CardTitle>
                  <CardDescription>Состояние обработки заявок</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {incidentsData.statusDistribution.map((status) => (
                    <div key={status.status} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">{status.label}</span>
                        <Badge variant="outline">{status.count}</Badge>
                      </div>
                      <Progress value={status.percentage} className="w-full" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Активность по дням
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {incidentsData.dailyActivity.length ? (
                    incidentsData.dailyActivity.map((day) => (
                      <div key={day.date} className="flex items-center justify-between border border-border rounded-lg p-3 mb-2 bg-muted/20">
                        <span className="text-sm">{new Date(day.date).toLocaleDateString("ru-RU")}</span>
                        <Badge variant="secondary">{day.count}</Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">Нет данных</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Активные пользователи
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {incidentsData.activeUsers.map((active) => (
                    <div key={active.userId} className="flex justify-between items-center border border-border rounded-lg p-3 bg-muted/20">
                      <div>
                        <p className="font-semibold text-sm">{active.userName}</p>
                        <p className="text-xs text-muted-foreground">Количество инцидентов</p>
                      </div>
                      <Badge variant="secondary">{active.count}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Системная активность
                </CardTitle>
                <CardDescription>Показатели использования платформы</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <p className="text-sm text-muted-foreground">Действий за сегодня</p>
                  <p className="text-3xl font-bold mt-2 text-foreground">{analyticsData.systemActivity.actionsToday}</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <p className="text-sm text-muted-foreground">За неделю</p>
                  <p className="text-3xl font-bold mt-2 text-foreground">{analyticsData.systemActivity.actionsThisWeek}</p>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <p className="text-sm text-muted-foreground">Время активности</p>
                  <div className="flex items-center gap-2 mt-2">
                    <p className="text-3xl font-bold text-foreground">
                      {analyticsData.systemActivity.activeTimePercentage}%
                    </p>
                    <span className="text-xs text-muted-foreground">в течение дня</span>
                  </div>
                  <Progress value={analyticsData.systemActivity.activeTimePercentage} className="mt-3" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Активность пользователей
                </CardTitle>
                <CardDescription>Статистика работы пользователей</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {analyticsData.userActivity.length ? (
                  analyticsData.userActivity.map((activity) => {
                    const roleName = activity.roleId 
                      ? roles.find(r => r.id === activity.roleId)?.name 
                      : null;
                    const displayName = activity.fullName || roleName || activity.username || activity.email || "Пользователь";
                    
                    return (
                      <div key={activity.userId} className="p-4 border border-border rounded-lg bg-card/50 hover:bg-muted/40 transition-colors">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="font-semibold text-lg">{displayName}</div>
                              {activity.isActive ? (
                                <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300">Активен</Badge>
                              ) : (
                                <Badge variant="secondary">Неактивен</Badge>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                              {activity.username && (
                                <div className="flex items-center gap-2">
                                  <UserIcon className="h-4 w-4" />
                                  {activity.username}
                                </div>
                              )}
                              {activity.email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-4 w-4" />
                                  {activity.email}
                                </div>
                              )}
                              {activity.lastLogin && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  Последний вход: {activity.lastLogin}
                                </div>
                              )}
                              {activity.registeredDate && (
                                <div className="flex items-center gap-2">
                                  <Edit className="h-4 w-4" />
                                  Регистрация: {activity.registeredDate}
                                </div>
                              )}
                              {activity.isActiveThisWeek && (
                                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                                  <BarChart3 className="h-4 w-4" />
                                  {activity.activityStatus}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right space-y-1 ml-4">
                            <Badge variant="outline" className="block">
                              {activity.objectsCreated} объектов
                            </Badge>
                            <Badge variant="outline" className="block">
                              {activity.geometriesCreated} геометрий
                            </Badge>
                            <div className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                              Всего: {activity.totalContributions}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Нет данных о пользователях</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="organizations" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Статистика органов управления
                </CardTitle>
                <CardDescription>Анализ данных по органам управления</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {analyticsData.organizationStats.length > 0 ? (
                  analyticsData.organizationStats.map((org) => (
                    <div key={org.id} className="p-4 border border-border rounded-lg bg-card/50 hover:bg-muted/40 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{org.name}</h3>
                          {org.description && (
                            <p className="text-sm text-muted-foreground mt-1">{org.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge 
                              variant={org.activityLevel === 'Высокая' ? 'default' : 
                                      org.activityLevel === 'Средняя' ? 'secondary' : 'outline'}
                              className={
                                org.activityLevel === 'Высокая' ? 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-300' :
                                org.activityLevel === 'Средняя' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-200' :
                                'bg-muted text-muted-foreground'
                              }
                            >
                              {org.activityLevel} активность
                            </Badge>
                            <Badge variant="outline">
                              ID: {org.id}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 rounded-lg border border-border bg-muted/30">
                          <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{org.objects}</div>
                          <div className="text-sm text-muted-foreground">Объектов</div>
                        </div>
                        <div className="text-center p-3 rounded-lg border border-border bg-muted/30">
                          <div className="text-xl font-bold text-green-600 dark:text-green-400">{org.geometries}</div>
                          <div className="text-sm text-muted-foreground">Геометрий</div>
                        </div>
                        <div className="text-center p-3 rounded-lg border border-border bg-muted/30">
                          <div className="text-xl font-bold text-purple-600 dark:text-purple-400">{org.users}</div>
                          <div className="text-sm text-muted-foreground">Пользователей</div>
                        </div>
                        <div className="text-center p-3 rounded-lg border border-border bg-muted/30">
                          <div className="text-xl font-bold text-orange-600 dark:text-orange-400">{org.activeUsers}</div>
                          <div className="text-sm text-muted-foreground">Активных</div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-3 border-t border-border">
                        <div className="flex justify-between items-center text-sm">
                          <div className="text-muted-foreground">
                            <span className="font-medium text-foreground">Всего вкладов:</span> {org.totalContributions}
                          </div>
                          <div className="text-muted-foreground">
                            <span className="font-medium text-foreground">Активность пользователей:</span> {org.userActivityRate}%
                          </div>
                        </div>
                        {org.userActivityRate > 0 && (
                          <div className="mt-2">
                            <Progress value={org.userActivityRate} className="h-2" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Building className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">Нет данных об органах управления</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

